import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { store } from '../data/store';
import { btnPrimary, btnSecondary } from '../components/ui/buttonStyles';
import { formatPrice } from '../utils/currency';
import { easeOut } from '../utils/motion';
import type { Order } from '../types/cart';
export function OrderConfirmation() {
  const location = useLocation();
  const reduce = useReducedMotion();
  const order = (location.state as {
    order?: Order;
  } | null)?.order;
  if (!order) {
    return <div className="mx-auto max-w-xl px-5 py-24 text-center" data-id="element-851" data-mp="18pebizdu374t">
        <h1 className="font-display text-4xl" data-id="element-852" data-mp="18ouurlzx6osd">Aucune commande à afficher</h1>
        <p className="mt-3 text-muted" data-id="element-853" data-mp="1cpy8o2yeqaqy">Vos confirmations de commande vous sont envoyées par e-mail.</p>
        <Link to="/boutique" className={`${btnPrimary} mt-8`} data-id="element-854" data-mp="3tetw3cmglzm0">
          Retour à la boutique
        </Link>
      </div>;
  }
  const pickup = order.shippingId === 'pickup';
  const nextSteps = pickup ? [{
    title: 'Préparation',
    text: 'Le créateur de permanence emballe vos pièces avec soin.'
  }, {
    title: 'Prête sous 24 h',
    text: `Un e-mail vous prévient à ${order.email} dès qu’elle vous attend.`
  }, {
    title: 'Retrait',
    text: `${store.address}, ${store.hours[0].days.toLowerCase()}, ${store.hours[0].time}.`
  }] : [{
    title: 'Préparation',
    text: 'Vos pièces sont emballées à la main en boutique.'
  }, {
    title: 'Expédition sous 2 jours',
    text: `Le numéro de suivi vous est envoyé à ${order.email}.`
  }, {
    title: 'Livraison',
    text: order.address ?? ''
  }];
  return <div className="mx-auto max-w-3xl px-5 pt-12 md:px-8 md:pt-20" data-id="element-855" data-mp="r3a9lbn7itki">
      <div className="text-center" data-id="element-856" data-mp="m2wc2nj8f20q">
        <svg viewBox="0 0 56 56" className="mx-auto h-14 w-14" aria-hidden data-id="element-857" data-mp="1025f0liffzmr">
          <motion.circle cx="28" cy="28" r="26" fill="none" strokeWidth="2" className="stroke-sage" initial={reduce ? false : {
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.3,
          ease: easeOut
        }} data-id="element-858" data-mp="102f9omef2uys" />
          <motion.path d="M18 29l7 7 13-15" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="stroke-sage" initial={reduce ? false : {
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.25,
          ease: easeOut,
          delay: 0.25
        }} data-id="element-859" data-mp="1qkxlr1wk533" />
        </svg>
        <h1 className="mt-6 font-display text-5xl leading-[1] md:text-6xl" data-id="element-860" data-mp="ldscrr2vdei8">Merci {order.firstName} !</h1>
        <p className="mt-4 text-muted" data-id="element-861" data-mp="pg70aohhh2fu">
          Commande <span className="font-medium text-ink" data-id="element-862" data-mp="pdt3ob75ni66">{order.number}</span> confirmée. Un récapitulatif vous a été envoyé
          par e-mail.
        </p>
      </div>

      <ol className="mt-14 grid gap-6 border-y border-line py-8 md:grid-cols-3" data-id="element-863" data-mp="2r8bl7g0kezh2">
        {nextSteps.map((s, i) => <li key={s.title} data-id="element-864" data-mp="2jpc2zdoubdza" data-mpi={i}>
            <span className={`text-xs font-medium ${i === 0 ? 'text-clay' : 'text-muted'}`} data-id="element-865" data-mp="2jplsl05z3j4g">{i === 0 ? 'En cours' : 'Ensuite'}</span>
            <p className="mt-1 font-display text-2xl" data-id="element-866" data-mp="29x6eidxsreou">{s.title}</p>
            <p className="mt-1 text-sm text-muted" data-id="element-867" data-mp="25wcpticj5vuw">{s.text}</p>
          </li>)}
      </ol>

      <section className="mt-10" aria-labelledby="recap-title" data-id="element-868" data-mp="25ujjy59qrfny">
        <h2 id="recap-title" className="text-sm font-medium" data-id="element-869" data-mp="32m3kpioqhl18">
          Votre commande
        </h2>
        <ul className="mt-4 divide-y divide-line" data-id="element-870" data-mp="2xo20gufknfo6">
          {order.lines.map(l => <li key={l.key} className="flex items-center gap-4 py-4" data-id="element-871" data-mp="2tkm4m0bbtjcj">
              <img src={l.product.images[0]} alt="" className="h-16 w-12 rounded-sm object-cover" data-id="element-872" data-mp="2ondpy9pdzxg1" />
              <div className="min-w-0 flex-1" data-id="element-873" data-mp="1i71x8o5c37ru">
                <p className="text-sm font-medium" data-id="element-874" data-mp="1i836cn2shk6x">{l.product.name}</p>
                <p className="text-xs text-muted" data-id="element-875" data-mp="1d6w0itk7iv2w">
                  Qté {l.quantity}
                  {l.option ? ` · ${l.option}` : ''}
                </p>
              </div>
              <p className="text-sm tabular-nums" data-id="element-876" data-mp="35vd80pqq526q">{formatPrice(l.product.price * l.quantity)}</p>
            </li>)}
        </ul>
        <dl className="mt-4 space-y-2 border-t border-line pt-4 text-sm" data-id="element-877" data-mp="2s59ow7n55oli">
          <div className="flex justify-between" data-id="element-878" data-mp="2w6bs0zk6iksr">
            <dt className="text-muted" data-id="element-879" data-mp="2w2xovp3oy7k9">Sous-total</dt>
            <dd data-id="element-880" data-mp="3jw659i8eubdj">{formatPrice(order.subtotal)}</dd>
          </div>
          {order.discount > 0 && <div className="flex justify-between text-sage" data-id="element-881" data-mp="3oso7obg9pxmp">
              <dt data-id="element-882" data-mp="3augca7yvr4dn">Réduction</dt>
              <dd data-id="element-883" data-mp="3at529qeud0e3">−{formatPrice(order.discount)}</dd>
            </div>}
          <div className="flex justify-between" data-id="element-884" data-mp="3fshxayfv9drr">
            <dt className="text-muted" data-id="element-885" data-mp="3fvdw0ozjg6h9">{order.shippingLabel}</dt>
            <dd data-id="element-886" data-mp="1zdzxmgv5grru">{order.shippingCost === 0 ? 'Offert' : formatPrice(order.shippingCost)}</dd>
          </div>
          {order.giftWrap && <div className="flex justify-between" data-id="element-887" data-mp="24c1fiygual1q">
              <dt className="text-muted" data-id="element-888" data-mp="1vw2emvdcdic1">Emballage cadeau</dt>
              <dd data-id="element-889" data-mp="1rvigboh188ob">Offert</dd>
            </div>}
          <div className="flex items-baseline justify-between border-t border-line pt-3" data-id="element-890" data-mp="1ruyy06ngzi9r">
            <dt className="font-medium" data-id="element-891" data-mp="25tyozkygg993">Total payé</dt>
            <dd className="font-display text-3xl" data-id="element-892" data-mp="20wgazg5kz3u8">{formatPrice(order.total)}</dd>
          </div>
        </dl>
      </section>

      <div className="mt-12 flex flex-wrap justify-center gap-3" data-id="element-893" data-mp="1d5uhqv08jo4a">
        <Link to="/boutique" className={btnPrimary} data-id="element-894" data-mp="187j74i2499l0">
          Continuer à flâner
        </Link>
        <Link to="/createurs?statut=upcoming" className={btnSecondary} data-id="element-895" data-mp="1mwfa40pbfwer">
          Voir les prochains créateurs
        </Link>
      </div>
    </div>;
}